import { supabase } from "@/integrations/supabase/client";
import { VariantImage, VariantImageMetadata, generateVariantImageKey } from "@/types/variantImage";
import { getImageUploadProvider } from "./imageUploadProvider";

export const variantImageService = {
  // Fetch all variant images for a product
  async fetchVariantImages(productId: string): Promise<VariantImage[]> {
    const { data, error } = await supabase
      .from("variant_images")
      .select("*")
      .eq("product_id", productId)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false });

    if (error) throw new Error(error.message);
    return data || [];
  },

  // Fetch all images for a specific variant (ordered by cover, sort_order, created_at)
  async fetchVariantImagesByVariantId(variantId: string): Promise<VariantImage[]> {
    const { data, error } = await supabase
      .from("variant_images")
      .select("*")
      .eq("variant_id", variantId)
      .order("is_cover", { ascending: false })
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });

    if (error) throw new Error(error.message);
    return data || [];
  },

  // Upload and save variant image
  async uploadVariantImage(
    file: File, 
    metadata: VariantImageMetadata
  ): Promise<VariantImage> {
    const provider = getImageUploadProvider();
    const uploadResult = await provider.upload(file, metadata);

    if (!uploadResult.success || !uploadResult.imageUrl) {
      throw new Error(uploadResult.error || "Upload failed");
    }

    const imageData = {
      product_id: metadata.productId,
      variant_id: metadata.variantId || null,
      pot_size: metadata.potSize,
      pot_type: metadata.potType,
      pot_color: metadata.potColor,
      image_url: uploadResult.imageUrl,
      metadata: {
        key: generateVariantImageKey(metadata),
        originalFileName: file.name,
        fileSize: file.size,
        contentType: file.type
      },
      uploaded_by: (await supabase.auth.getUser()).data.user?.id
    };

    const { data, error } = await supabase
      .from("variant_images")
      .insert(imageData)
      .select()
      .single();

    if (error) {
      // Clean up uploaded file if database insert fails
      try {
        await provider.delete(uploadResult.imageUrl);
      } catch (deleteError) {
        console.warn("Failed to clean up uploaded file:", deleteError);
      }
      throw new Error(error.message);
    }

    return data;
  },

  // Upload image once and link to multiple variants atomically
  async uploadAndLinkToVariants(
    file: File,
    productId: string,
    variantIds: string[],
    options?: { altText?: string; sortOrder?: number }
  ): Promise<VariantImage[]> {
    if (variantIds.length === 0) {
      throw new Error("At least one variant must be selected");
    }

    const provider = getImageUploadProvider();
    
    // Fetch ALL variant details
    const { data: variants, error: variantsError } = await supabase
      .from("product_variants")
      .select("id, size, pot_type, pot_color")
      .in("id", variantIds);

    if (variantsError || !variants || variants.length === 0) {
      throw new Error("Variants not found");
    }

    // Use first variant for upload path
    const firstVariant = variants[0];
    const metadata: VariantImageMetadata = {
      productId,
      potSize: firstVariant.size,
      potType: firstVariant.pot_type,
      potColor: firstVariant.pot_color
    };

    // Upload once
    const uploadResult = await provider.upload(file, metadata);
    if (!uploadResult.success || !uploadResult.imageUrl) {
      throw new Error(uploadResult.error || "Upload failed");
    }

    const uploadedBy = (await supabase.auth.getUser()).data.user?.id;

    // Create DB records for each variant using its own attributes
    const imageRecords = variants.map(variant => ({
      product_id: productId,
      variant_id: variant.id,
      pot_size: variant.size,
      pot_type: variant.pot_type,
      pot_color: variant.pot_color,
      image_url: uploadResult.imageUrl,
      alt_text: options?.altText || null,
      sort_order: options?.sortOrder || 0,
      metadata: {
        key: `${variant.pot_color}:${variant.size}:${variant.pot_type}`,
        originalFileName: file.name,
        fileSize: file.size,
        contentType: file.type
      },
      uploaded_by: uploadedBy
    }));

    const { data, error } = await supabase
      .from("variant_images")
      .insert(imageRecords)
      .select();

    if (error) {
      // Rollback: delete uploaded file
      try {
        await provider.delete(uploadResult.imageUrl);
      } catch (deleteError) {
        console.warn("Failed to clean up uploaded file after DB error:", deleteError);
      }
      throw new Error(error.message);
    }

    return data || [];
  },

  // Delete variant image
  async deleteVariantImage(imageId: string): Promise<void> {
    // First get the image to delete from storage
    const { data: image, error: fetchError } = await supabase
      .from("variant_images")
      .select("image_url")
      .eq("id", imageId)
      .single();

    if (fetchError) throw new Error(fetchError.message);

    // Delete from storage
    const provider = getImageUploadProvider();
    await provider.delete(image.image_url);

    // Delete from database
    const { error } = await supabase
      .from("variant_images")
      .delete()
      .eq("id", imageId);

    if (error) throw new Error(error.message);
  },

  // Update variant image metadata
  async updateVariantImage(
    imageId: string, 
    updates: Partial<Omit<VariantImage, 'id' | 'created_at' | 'updated_at'>>
  ): Promise<VariantImage> {
    const { data, error } = await supabase
      .from("variant_images")
      .update(updates)
      .eq("id", imageId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data;
  },

  // Set image as cover (trigger will handle unsetting others)
  async setCoverImage(imageId: string, variantId: string): Promise<void> {
    const { error } = await supabase
      .from("variant_images")
      .update({ is_cover: true })
      .eq("id", imageId)
      .eq("variant_id", variantId);

    if (error) throw new Error(error.message);
  },

  // Reorder images for a variant
  async reorderImages(variantId: string, imageIdsInOrder: string[]): Promise<void> {
    const updates = imageIdsInOrder.map((imageId, index) => 
      supabase
        .from("variant_images")
        .update({ sort_order: index })
        .eq("id", imageId)
        .eq("variant_id", variantId)
    );

    const results = await Promise.all(updates);
    const errors = results.filter(r => r.error);
    if (errors.length > 0) {
      throw new Error(`Failed to reorder ${errors.length} images`);
    }
  },

  // Get images by variant attributes
  async getImagesByAttributes(
    productId: string,
    potSize: string,
    potType: string,
    potColor: string
  ): Promise<VariantImage[]> {
    const { data, error } = await supabase
      .from("variant_images")
      .select("*")
      .eq("product_id", productId)
      .eq("pot_size", potSize)
      .eq("pot_type", potType)
      .eq("pot_color", potColor)
      .order("is_cover", { ascending: false })
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });

    if (error) throw new Error(error.message);
    return data || [];
  },

  // Bulk upload images
  async bulkUploadImages(
    files: File[],
    commonMetadata: Omit<VariantImageMetadata, 'potSize' | 'potType' | 'potColor'>,
    attributesPerFile: Array<{ potSize: string; potType: string; potColor: string }>
  ): Promise<VariantImage[]> {
    const results: VariantImage[] = [];
    const errors: string[] = [];

    for (let i = 0; i < files.length; i++) {
      try {
        const metadata = {
          ...commonMetadata,
          ...attributesPerFile[i]
        };
        const result = await this.uploadVariantImage(files[i], metadata);
        results.push(result);
      } catch (error) {
        errors.push(`File ${files[i].name}: ${error instanceof Error ? error.message : 'Upload failed'}`);
      }
    }

    if (errors.length > 0) {
      console.warn("Some uploads failed:", errors);
    }

    return results;
  }
};